import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { EmptyDiv, FieldTitle, HorizontalDiv, ImgButton } from '../../../../../components/Common';
import { canEditGrowing } from '../../../../../service/PermissionService';
import { updateTechFormCoordinates } from '../../../../../service/TechFormDetailService';
import EditableInfoText from '../../CrAlerts/EditableInfoText';
import colors from '../../../../../res/styles/Colors';
import dimension from '../../../../../res/styles/Responsive';
import { toastError } from '../../../../../ui/ToastHelper';

import { ReactComponent as DeleteIcon } from '../../../../../res/assets/delete.svg';
import { ReactComponent as FlagIcon } from '../../../../../res/assets/flag_map.svg';
import { ReactComponent as AddLocationIcon } from '../../../../../res/assets/add_location.svg';
import { ReactComponent as EditIcon } from '../../../../../res/assets/cac_edit.svg';
import { ReactComponent as CheckIcon } from '../../../../../res/assets/checkIcon.svg';
import { ReactComponent as CancelIcon } from '../../../../../res/assets/cancelIconX.svg';

Coordinates.propTypes = {
    techFormPk: PropTypes.number,
    coordinates: PropTypes.arrayOf(
        PropTypes.shape({
            latitude: PropTypes.number,
            longitude: PropTypes.number
        })
    ),
    isEditable: PropTypes.bool
};

Coordinates.defaultProps = {
    isEditable: false,
    coordinates: []
};

export default function Coordinates({ techFormPk, coordinates, isEditable }) {
    const [isEditableState, setIsEditableState] = useState(isEditable);
    const [coords, setCoords] = useState(coordinates || []);
    const [latestSavedCoords, setLatestSavedCoords] = useState([...(coordinates || [])]);

    useEffect(() => {
        setIsEditableState(isEditable);
    }, [isEditable]);

    useEffect(() => {
        setCoords(coordinates || []);
        setLatestSavedCoords([...(coordinates || [])]);
    }, [coordinates]);

    const onSave = () => {
        // Filter out coordinates with empty latitude or longitude
        const validCoords = coords.filter(
            (coord) => coord.latitude != null && coord.longitude != null
        );
        updateTechFormCoordinates(validCoords, techFormPk, (updatedCoordinates) => {
            if (updatedCoordinates) {
                setCoords(updatedCoordinates);
                setLatestSavedCoords([...updatedCoordinates]);
                setIsEditableState(false);
            } else {
                // Show error notification
                toastError('Failed to save coordinates. Changes have been discarded.');
                // Restore initial values
                setCoords([...latestSavedCoords]);
                // Exit edit mode
                setIsEditableState(false);
            }
        });
    };

    const onCancel = () => {
        setCoords([...latestSavedCoords]);
        setIsEditableState(false);
    };

    const onAddCoordinate = () => {
        setCoords([...coords, { latitude: null, longitude: null }]);
    };

    const onDeleteCoordinate = (index) => {
        const newCoords = coords.filter((_, i) => i !== index);
        setCoords(newCoords);
    };

    const onUpdateCoordinate = (index, field, value) => {
        const newCoords = [...coords];
        newCoords[index] = {
            ...newCoords[index],
            [field]: value ? parseFloat(value) : null
        };
        setCoords(newCoords);
    };

    const openInGoogleMaps = (latitude, longitude) => {
        if (latitude && longitude) {
            const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
            window.open(url, '_blank');
        }
    };

    return (
        <CoordinatesContainer>
            <HeaderContainer>
                <FieldTitle>Geo Coord</FieldTitle>
                <EmptyDiv width='10px' height='10px' />
                {!isEditableState ? (
                    canEditGrowing() && (
                        <EditIconButton
                            onClick={() => setIsEditableState(true)}
                            title='Edit coordinates'>
                            <EditIcon />
                        </EditIconButton>
                    )
                ) : (
                    <HeaderButtonsContainer>
                        <SaveIconButton onClick={onSave} title='Save coordinates'>
                            <CheckIcon />
                        </SaveIconButton>
                        <CancelIconButton onClick={onCancel} title='Cancel editing'>
                            <CancelIcon />
                        </CancelIconButton>
                    </HeaderButtonsContainer>
                )}
            </HeaderContainer>

            {coords.length === 0 && !isEditableState && (
                <NoCoordinatesText>-</NoCoordinatesText>
            )}

            {coords.map((coord, index) => (
                <CoordinateRow key={index}>
                    {!isEditableState ? (
                        <>
                            <ViewModeContainer>
                                <FlagIcon />
                                <CoordinateText>
                                    {coord.latitude != null && coord.longitude != null
                                        ? `${coord.latitude}, ${coord.longitude}`
                                        : '-'}
                                </CoordinateText>
                            </ViewModeContainer>
                            {coord.latitude != null && coord.longitude != null && (
                                <MapLink
                                    onClick={() => openInGoogleMaps(coord.latitude, coord.longitude)}
                                    title='Open in Google Maps'>
                                    view on Maps
                                </MapLink>
                            )}
                        </>
                    ) : (
                        <>
                            <CoordinateFields>
                                <CoordinateLabel>Lat:</CoordinateLabel>
                                <StyledEditableInfoText
                                    initialValue={coord.latitude || '-'}
                                    isEditable={isEditableState}
                                    onFieldEdited={(_, value) =>
                                        onUpdateCoordinate(index, 'latitude', value)
                                    }
                                />
                                <FieldSpacer width='10px' />
                                <CoordinateLabel>Long:</CoordinateLabel>
                                <StyledEditableInfoText
                                    initialValue={coord.longitude || '-'}
                                    isEditable={isEditableState}
                                    onFieldEdited={(_, value) =>
                                        onUpdateCoordinate(index, 'longitude', value)
                                    }
                                />
                            </CoordinateFields>

                            <ActionsContainer>
                                <DeleteIconButton
                                    onClick={() => onDeleteCoordinate(index)}
                                    title='Delete coordinate'>
                                    <DeleteIcon />
                                </DeleteIconButton>
                            </ActionsContainer>
                        </>
                    )}
                </CoordinateRow>
            ))}

            {isEditableState && (
                <AddButtonContainer>
                    <AddCoordinateButton onClick={onAddCoordinate} title='Add new coordinate'>
                        <AddLocationIcon />
                        Add coordinate
                    </AddCoordinateButton>
                </AddButtonContainer>
            )}
        </CoordinatesContainer>
    );
}

const CoordinatesContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const HeaderContainer = styled(HorizontalDiv)`
    align-items: center;
    margin-bottom: 0;
    
    ${FieldTitle} {
        margin-bottom: 0;
    }
    
    @media (max-width: 767px) {
        gap: 8px;
        flex-wrap: wrap;
    }
`;

const HeaderButtonsContainer = styled(HorizontalDiv)`
    align-items: center;
    gap: 15px;
`;

const CoordinateRow = styled(HorizontalDiv)`
    align-items: center;
    padding: 4px 0;
    gap: 15px;
    
    @media (max-width: 767px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        padding: 8px 0;
        border-bottom: 1px solid #f0f0f0;
    }
`;

const CoordinateFields = styled(HorizontalDiv)`
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    
    @media ${dimension.md} {
        width: auto;
        flex-direction: row;
        align-items: center;
        gap: 0;
    }
`;

const FieldSpacer = styled(EmptyDiv)`
    display: none;
    
    @media ${dimension.md} {
        display: block;
    }
`;

const CoordinateLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.darker_gray};
    min-width: auto;
    margin-right: 0;
    margin-bottom: 2px;
    width: 100%;
    
    @media ${dimension.md} {
        font-size: 12px;
        margin-right: 5px;
        margin-bottom: 0;
        min-width: 35px;
        width: auto;
    }
`;

const ActionsContainer = styled(HorizontalDiv)`
    align-items: center;
    gap: 10px;
    width: 100%;
    justify-content: center;
    margin-top: 0;
    
    @media ${dimension.md} {
        width: auto;
        justify-content: flex-start;
        margin-top: 0;
    }
`;

const AddButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 10px;
`;

const NoCoordinatesText = styled.div`
    font-size: 12px;
    color: ${colors.darker_gray};
    margin-top: -8px;
`;

const StyledEditableInfoText = styled(EditableInfoText).attrs({
    type: 'number'
})`
    font-size: 11px;
    width: 70%;
    margin-bottom: 0px;
    
    @media ${dimension.md} {
        font-size: 12px;
        width: 80px;
        margin-bottom: 0;
    }
`;

const ViewModeContainer = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    
    svg {
        width: 12px;
        height: 14px;
        align-self: center;
    }
    
    @media (max-width: 767px) {
        flex: 1;
        min-width: 0;
    }
`;

const CoordinateText = styled.span`
    font-size: 12px;
    color: ${colors.darkest_gray};
    word-break: break-word;
    
    @media (max-width: 767px) {
        font-size: 11px;
    }
`;

const MapLink = styled.span`
    font-size: 12px;
    color: ${colors.orange};
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    
    &:hover {
        text-decoration: underline;
    }
    
    @media (max-width: 767px) {
        font-size: 11px;
        margin-left: 20px;
    }
`;

const IconButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    
    &:hover {
        opacity: 0.8;
    }
`;

const AddCoordinateButton = styled(IconButton)`
    color: ${colors.orange};
    font-size: 12px;
    gap: 6px;
    transition: opacity 0.2s;

    svg {
        width: 14px;
        height: 16px;
    }
`;

const EditIconButton = styled(IconButton)`
    svg {
        width: 16px;
        height: 16px;
        color: ${colors.green};
    }
`;

const SaveIconButton = styled(IconButton)`
    svg {
        width: 15px;
        height: 15px;
        
        path {
            fill: ${colors.green};
        }
    }
    
    &:hover svg path {
        fill: ${colors.dark_green};
    }
`;

const CancelIconButton = styled(IconButton)`
    svg {
        width: 13px;
        height: 13px;
        
        path {
            fill: ${colors.red};
        }
    }
    
    &:hover svg path {
        fill: ${colors.dark_red};
    }
`;

const DeleteIconButton = styled(IconButton)`
    svg {
        width: 14px;
        height: 14px;
        fill: ${colors.red};
        transition: fill 0.2s;
        
        path {
            fill: ${colors.red};
        }
    }
    
    &:hover {
        opacity: 1;
        
        svg {
            fill: ${colors.dark_red};
            
            path {
                fill: ${colors.dark_red};
            }
        }
    }
`;
